/* MONAD flow — composer → an orchestrated, branching investigation.
   MONAD deploys OPERATIVES. A Download operative gathers every dataset. Analysis
   operatives chain off the data they consume, running real pipeline steps with a
   clear input → steps → output → finding. The camera follows the work up the
   branches, then pans to the bottom for the report + figures at the end.

   Ported from the standalone flow-animation script into a single entry point:
   runFlow(stage) wires up the (already-rendered) scaffold and returns a cleanup. */
import { makePointCloud } from './MonadSigil';

const NS = 'http://www.w3.org/2000/svg';
const QUESTION = 'What drives mitochondrial decline in aged cardiac tissue?';
const VB_W = 1000;
const VB_H = 640;
const ROOT = { x: 500, y: 330 };
const CARD_W = 162;
const CARD_H = 72;
const STORAGE = 128;

const CHEV =
  '<span class="WSMONAD-chev"><svg viewBox="0 0 16 16"><path d="M6 4l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';
const FOLDER_IC =
  '<span class="WSMONAD-fic"><svg viewBox="0 0 16 16"><path d="M1.5 4.5C1.5 3.9 2 3.4 2.6 3.4h3.1l1.3 1.5h5.4c.6 0 1.1.5 1.1 1.1v5.5c0 .6-.5 1.1-1.1 1.1H2.6c-.6 0-1.1-.5-1.1-1.1z" fill="currentColor" opacity="0.18"/><path d="M1.5 4.5C1.5 3.9 2 3.4 2.6 3.4h3.1l1.3 1.5h5.4c.6 0 1.1.5 1.1 1.1v5.5c0 .6-.5 1.1-1.1 1.1H2.6c-.6 0-1.1-.5-1.1-1.1z" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linejoin="round"/></svg></span>';
const FILE_IC =
  '<span class="WSMONAD-fic"><svg viewBox="0 0 16 16"><path d="M4 2.5h5L12 5.5V13a.5.5 0 01-.5.5h-7A.5.5 0 014 13z" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linejoin="round"/><path d="M9 2.5V6h3" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linejoin="round"/></svg></span>';
const PROBE_IC =
  '<svg viewBox="0 0 16 16"><circle cx="8" cy="8" r="5.4" fill="none" stroke="currentColor" stroke-width="1.2"/><circle cx="8" cy="8" r="1.7" fill="currentColor"/><path d="M8 .8v2.2M8 13v2.2M.8 8h2.2M13 8h2.2" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>';

const ICON = {
  PULL: '<svg viewBox="0 0 16 16"><ellipse cx="8" cy="4" rx="5.5" ry="2.2" fill="none" stroke="currentColor" stroke-width="1.2"/><path d="M2.5 4v8c0 1.2 2.5 2.2 5.5 2.2s5.5-1 5.5-2.2V4" fill="none" stroke="currentColor" stroke-width="1.2"/><path d="M2.5 8c0 1.2 2.5 2.2 5.5 2.2S13.5 9.2 13.5 8" fill="none" stroke="currentColor" stroke-width="1.2"/></svg>',
  COMPUTE: '<svg viewBox="0 0 16 16"><rect x="4.5" y="4.5" width="7" height="7" rx="1" fill="none" stroke="currentColor" stroke-width="1.2"/><path d="M6.5 2v2M9.5 2v2M6.5 12v2M9.5 12v2M2 6.5h2M2 9.5h2M12 6.5h2M12 9.5h2" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/></svg>',
  SEARCH: '<svg viewBox="0 0 16 16"><circle cx="7" cy="7" r="4.2" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M10.2 10.2L14 14" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>',
  MODEL: '<svg viewBox="0 0 16 16"><circle cx="3.5" cy="8" r="1.6" fill="currentColor"/><circle cx="12.5" cy="4" r="1.6" fill="currentColor"/><circle cx="12.5" cy="12" r="1.6" fill="currentColor"/><path d="M5 8L11 4.5M5 8l6 3.5" stroke="currentColor" stroke-width="1.1"/></svg>',
  REPORT: '<svg viewBox="0 0 16 16"><path d="M4 2h5l3 3v9H4z" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><path d="M9 2v3h3M6 8h4M6 10.5h4" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/></svg>',
  CHART: '<svg viewBox="0 0 16 16"><path d="M2.5 13.5h11" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/><rect x="4" y="8" width="2.2" height="4.2" fill="currentColor"/><rect x="7.4" y="5" width="2.2" height="7.2" fill="currentColor"/><rect x="10.8" y="9.5" width="2.2" height="2.7" fill="currentColor"/></svg>',
};
const VIZ = {
  PULL: '<div class="WSMONAD-viz-bar"><span></span></div>',
  COMPUTE: '<div class="WSMONAD-viz-eq"><i></i><i></i><i></i><i></i><i></i></div>',
  MODEL: '<div class="WSMONAD-viz-net"><i></i><i></i><i></i><i></i></div>',
  FILES: '<div class="WSMONAD-viz-files"><div class="WSMONAD-vf">deseq2_results.tsv</div><div class="WSMONAD-vf">enrichment.tsv</div><div class="WSMONAD-vf">validation.tsv</div><div class="WSMONAD-vf">vep_annotated.vcf</div><div class="WSMONAD-vf WSMONAD-fig">figures.svg</div></div>',
  SEARCH: '<div class="WSMONAD-viz-book"><div class="WSMONAD-bk"><span class="WSMONAD-cover"></span><span class="WSMONAD-spine"></span><span class="WSMONAD-pg"></span><span class="WSMONAD-pg"></span><span class="WSMONAD-pg"></span></div><div class="WSMONAD-bk-out"><span class="WSMONAD-bk-arrow">→ 6 relevant</span><span class="WSMONAD-bk-chip"><span class="WSMONAD-bk-n">0</span> / 412 screened</span></div></div>',
  REPORT: '<div class="WSMONAD-rdoc"><div class="WSMONAD-rdoc-h">SIRT3 &rarr; cardiac aging</div><div class="WSMONAD-rdoc-lines"><i></i><i></i><i></i><i></i></div></div>',
  CHART: '<div class="WSMONAD-plotcode"><span class="WSMONAD-pc"><b>›</b> plt.volcano(degs)</span><span class="WSMONAD-pc"><b>›</b> plt.bars(pathways)</span><span class="WSMONAD-pc"><b>›</b> plt.scatter(cohort)</span><span class="WSMONAD-pc"><b>›</b> nx.draw(network)</span></div>',
};
const TERMINAL_TYPES = ['COMPUTE', 'MODEL'];

// figure panels the plotting island renders one-by-one, fanned around it.
// dx/dy are offsets from the chart island's center.
const PLOTS = [
  { t: 'volcano', dx: -188, dy: 120, src: 'deseq2_results.tsv',
    svg: '<svg viewBox="0 0 104 66" preserveAspectRatio="none"><line class="WSMONAD-pax" x1="15" y1="6" x2="15" y2="54"/><line class="WSMONAD-pax" x1="15" y1="54" x2="98" y2="54"/><line class="WSMONAD-pthr" x1="56" y1="6" x2="56" y2="54"/><line class="WSMONAD-pthr" x1="15" y1="22" x2="98" y2="22"/><circle class="WSMONAD-pd" cx="30" cy="46" r="1.7"/><circle class="WSMONAD-pd" cx="38" cy="42" r="1.7"/><circle class="WSMONAD-pd" cx="45" cy="48" r="1.7"/><circle class="WSMONAD-pd" cx="51" cy="40" r="1.7"/><circle class="WSMONAD-pd" cx="60" cy="45" r="1.7"/><circle class="WSMONAD-pd" cx="66" cy="49" r="1.7"/><circle class="WSMONAD-pd" cx="72" cy="43" r="1.7"/><circle class="WSMONAD-pd" cx="58" cy="35" r="1.7"/><circle class="WSMONAD-pd" cx="43" cy="34" r="1.7"/><circle class="WSMONAD-phot" cx="22" cy="16" r="2.3"/><circle class="WSMONAD-phot" cx="27" cy="11" r="2.3"/><circle class="WSMONAD-phot" cx="20" cy="20" r="2.3"/><circle class="WSMONAD-phot" cx="86" cy="12" r="2.3"/><circle class="WSMONAD-phot" cx="90" cy="17" r="2.3"/><circle class="WSMONAD-phot" cx="82" cy="19" r="2.3"/></svg>' },
  { t: 'pathways', dx: -66, dy: 196, src: 'enrichment.tsv',
    svg: '<svg viewBox="0 0 104 66" preserveAspectRatio="none"><line class="WSMONAD-pax" x1="40" y1="8" x2="40" y2="56"/><rect class="WSMONAD-pbar" x="40" y="11" width="52" height="6"/><rect class="WSMONAD-pbar" x="40" y="22" width="42" height="6"/><rect class="WSMONAD-pbar" x="40" y="33" width="33" height="6"/><rect class="WSMONAD-pbar" x="40" y="44" width="23" height="6"/><line class="WSMONAD-ptick" x1="16" y1="14" x2="38" y2="14"/><line class="WSMONAD-ptick" x1="22" y1="25" x2="38" y2="25"/><line class="WSMONAD-ptick" x1="19" y1="36" x2="38" y2="36"/><line class="WSMONAD-ptick" x1="25" y1="47" x2="38" y2="47"/></svg>' },
  { t: 'cohort', dx: 64, dy: 180, src: 'validation.tsv',
    svg: '<svg viewBox="0 0 104 66" preserveAspectRatio="none"><line class="WSMONAD-pax" x1="15" y1="6" x2="15" y2="54"/><line class="WSMONAD-pax" x1="15" y1="54" x2="98" y2="54"/><line class="WSMONAD-ptrend" x1="18" y1="48" x2="94" y2="12"/><circle class="WSMONAD-pd" cx="24" cy="47" r="1.8"/><circle class="WSMONAD-pd" cx="32" cy="44" r="1.8"/><circle class="WSMONAD-pd" cx="40" cy="39" r="1.8"/><circle class="WSMONAD-pd" cx="48" cy="37" r="1.8"/><circle class="WSMONAD-pd" cx="56" cy="31" r="1.8"/><circle class="WSMONAD-pd" cx="64" cy="28" r="1.8"/><circle class="WSMONAD-pd" cx="72" cy="23" r="1.8"/><circle class="WSMONAD-pd" cx="80" cy="20" r="1.8"/><circle class="WSMONAD-phot" cx="44" cy="35" r="2.1"/><circle class="WSMONAD-phot" cx="68" cy="25" r="2.1"/></svg>' },
  { t: 'network', dx: 190, dy: 152, src: 'coexpr_network.graphml',
    svg: '<svg viewBox="0 0 104 66" preserveAspectRatio="none"><line class="WSMONAD-pedge" x1="54" y1="34" x2="28" y2="16"/><line class="WSMONAD-pedge" x1="54" y1="34" x2="82" y2="16"/><line class="WSMONAD-pedge" x1="54" y1="34" x2="22" y2="46"/><line class="WSMONAD-pedge" x1="54" y1="34" x2="84" y2="48"/><line class="WSMONAD-pedge" x1="54" y1="34" x2="54" y2="12"/><line class="WSMONAD-pedge" x1="28" y1="16" x2="22" y2="46"/><line class="WSMONAD-pedge" x1="82" y1="16" x2="84" y2="48"/><circle class="WSMONAD-phot" cx="54" cy="34" r="4"/><circle class="WSMONAD-pd" cx="28" cy="16" r="2.3"/><circle class="WSMONAD-pd" cx="82" cy="16" r="2.3"/><circle class="WSMONAD-pd" cx="22" cy="46" r="2.3"/><circle class="WSMONAD-pd" cx="84" cy="48" r="2.3"/><circle class="WSMONAD-pd" cx="54" cy="12" r="2.3"/></svg>' },
];

// citations the report references, shown as pills to its left as it composes
const CITES = ['deseq2_results.tsv', 'vep_annotated.vcf', 'evidence.md · 6 refs', 'validation.tsv'];

const PROBES = [
  { id: 'op_data', x: 500, y: 178, label: 'Download operative' },
  { id: 'op_lit', x: 760, y: 300, label: 'Literature operative' },
  { id: 'op_viz', x: 288, y: 520, label: 'Visualization operative', late: true },
  { id: 'op_report', x: 700, y: 520, label: 'Report operative', late: true },
];
const prById = {};
PROBES.forEach((p) => {
  prById[p.id] = p;
});

// islands. parent = operative id OR another island id (chain that pulls its data).
// io: [inputLabel, outputLabel] ; found: the plain-language result.
const ISLANDS = [
  { id: 'gtex', x: 300, y: 112, type: 'PULL', parent: 'op_data', plan: 'p1',
    name: 'GTEx · heart', dl: { name: 'gtex_v8_heart.tar.gz', size: '2.4 GB' }, out: 'expression matrix', file: ['database_downloads', 'gtex_v8_heart.tar.gz', '2.4 GB'] },
  { id: 'clinvar', x: 515, y: 92, type: 'PULL', parent: 'op_data', plan: 'p1',
    name: 'ClinVar · variants', dl: { name: 'clinvar_2024.vcf.gz', size: '1.1 GB' }, out: 'variant catalog', file: ['database_downloads', 'clinvar_2024.vcf.gz', '1.1 GB'] },
  { id: 'intake', x: 715, y: 120, type: 'PULL', parent: 'op_data', plan: 'p1',
    name: 'user_uploads · intake', dl: { name: 'aged_cohort.fastq', size: '6.2 GB' }, out: '64 samples indexed', file: ['workspace', 'cohort_index.json', '0.1 GB'], note: 'datasets gathered' },

  { id: 'deseq', x: 250, y: -128, type: 'COMPUTE', parent: 'gtex', plan: 'p2',
    name: 'Differential expression',
    io: ['gtex_v8_heart.tar.gz', 'deseq2_results.tsv'],
    term: [['$ ', 'tar -xzf gtex_v8_heart.tar.gz'], ['$ ', 'salmon quant → counts (432 hearts)'], ['$ ', 'DESeq2: counts ~ age'], ['out', '312 genes shift with age']],
    found: '312 genes change with age — SIRT3 down 38% in the oldest hearts.',
    out: 'SIRT3 ↓38%', file: ['workspace', 'deseq2_results.tsv', '48 MB'], note: 'SIRT3 falls with age' },
  { id: 'enrich', x: 150, y: -310, type: 'COMPUTE', parent: 'deseq', plan: 'p2',
    name: 'Pathway enrichment',
    io: ['deseq2_results.tsv', 'enrichment.tsv'],
    term: [['$ ', 'fgsea(ranked_DEGs, reactome)'], ['', 'scoring 1,604 gene sets…'], ['out', 'OXPHOS + mitophagy depleted']],
    found: 'The down genes concentrate in oxidative-phosphorylation and mitophagy pathways.',
    out: 'mito pathways depleted', file: ['workspace', 'enrichment.tsv', '2 MB'], note: 'mitochondrial pathways hit' },
  { id: 'coexpr', x: 372, y: -310, type: 'COMPUTE', parent: 'deseq', plan: 'p2',
    name: 'Co-expression network',
    io: ['deseq2_results.tsv', 'coexpr_network.graphml'],
    term: [['$ ', 'WGCNA::blockwiseModules(expr)'], ['', '24 modules detected'], ['out', 'SIRT3 is a hub of the mito module']],
    found: 'SIRT3 sits at the hub of the mitochondrial module — a likely driver, not a bystander.',
    out: 'SIRT3 = hub gene', file: ['workspace', 'coexpr_network.graphml', '64 MB'], note: 'SIRT3 is a network hub' },

  { id: 'vep', x: 628, y: -120, type: 'COMPUTE', parent: 'clinvar', plan: 'p3',
    name: 'Annotate variants (VEP)',
    io: ['clinvar_2024.vcf.gz', 'vep_annotated.vcf'],
    term: [['$ ', 'tabix -p vcf clinvar_2024.vcf.gz'], ['$ ', 'vep --offline --everything'], ['out', '2 missense hits in SIRT3']],
    found: 'Two coding variants land in SIRT3 itself (rs2228671, rs11247947).',
    out: '2 missense in SIRT3', file: ['workspace', 'vep_annotated.vcf', '0.9 GB'], note: '2 SIRT3 variants found' },
  { id: 'model', x: 858, y: -176, type: 'MODEL', parent: 'vep', plan: 'p3',
    name: 'Variant effect (ESM-2)',
    io: ['vep_annotated.vcf', 'variant_effects.parquet'],
    term: [['$ ', 'esm2.score(["rs2228671","rs11247947"])'], ['', 'ΔΔG = +2.1, +1.7 kcal/mol'], ['out', 'both destabilize the protein']],
    found: 'Both variants destabilize the SIRT3 protein, lowering its activity.',
    out: 'destabilizing', file: ['workspace', 'variant_effects.parquet', '12 MB'], note: 'variants destabilizing' },

  { id: 'validate', x: 958, y: 120, type: 'COMPUTE', parent: 'intake', plan: 'p4',
    name: 'Validate in your cohort',
    io: ['cohort_index.json + DEGs', 'validation.tsv'],
    term: [['$ ', 'join(your_cohort, age_DEGs)'], ['$ ', 'lm(SIRT3 ~ age + sex)'], ['out', 'SIRT3 −41% (p = 2e-5)']],
    found: 'The same SIRT3 decline holds in your 64 patients (−41%, p = 2e-5).',
    out: 'holds in your data', file: ['workspace', 'validation.tsv', '3 MB'], note: 'reproduced in your data' },

  { id: 'lit', x: 1030, y: 300, type: 'SEARCH', parent: 'op_lit', plan: 'p5', w: 250, h: 156, dur: 9000, screen: 412,
    name: 'Literature scan',
    terms: ['SIRT3', 'cardiac aging', 'mitophagy', 'OXPHOS'],
    refs: ['Nakamura 2021 · Circ Res', 'Diguet 2020 · Circulation', 'Zhang 2019 · Nat Aging', 'Lee 2022 · J Mol Cell Cardiol', 'Pillai 2017 · J Biol Chem', 'Sundaresan 2009 · J Clin Invest'],
    found: '6 of 47 papers independently link SIRT3 loss to cardiac aging.',
    out: '6 / 47 corroborate', file: ['workspace', 'evidence.md', '1 MB'], note: '6 papers support' },

  // combine node: TWO outputs (network hub + variant effect) joined into one call
  { id: 'integrate', x: 648, y: -352, type: 'MODEL', parents: ['coexpr', 'model'], plan: 'p9', focus: true, focusZoom: 1.18,
    name: 'Integrate hub + variant',
    io: ['coexpr_network + variant_effects', 'target_call.json'],
    term: [['$ ', 'intersect(hub_genes, destabilizing)'], ['', 'SIRT3 ∈ both sets'], ['out', 'SIRT3 = actionable target']],
    found: 'SIRT3 is both the network hub and the variant-hit gene — one actionable target.',
    out: 'SIRT3 actionable', file: ['workspace', 'target_call.json', '1 MB'], note: 'target confirmed' },

  { id: 'muscle', x: 70, y: 150, type: 'PULL', parent: 'op_data', steered: true, plan: 'p7',
    name: 'GTEx · skeletal muscle', dl: { name: 'gtex_muscle.tar.gz', size: '1.8 GB' }, out: 'no change → cardiac-specific', file: ['database_downloads', 'gtex_muscle.tar.gz', '1.8 GB'], note: 'cardiac-specific confirmed' },

  { id: 'report', x: 700, y: 712, type: 'REPORT', parent: 'op_report', plan: 'p8', late: true, w: 232, h: 134,
    name: 'Write report',
    found: 'A written report with the full chain of evidence and 4 figures.',
    out: 'report.md ready', file: ['workspace', 'report.md', '2 MB'], note: 'report drafted' },
  { id: 'chart', x: 330, y: 712, type: 'CHART', parent: 'op_viz', plan: 'p8', late: true, w: 236, h: 124, dur: 2300,
    name: 'Plot figures',
    src: 'deseq2 · validation · coexpr',
    found: '4 figures: SIRT3-vs-age, pathway bars, cohort replication, network.',
    out: '4 figures saved', file: ['workspace', 'figures.svg', '8 MB'], note: 'figures rendered' },
];
const byId = {};
ISLANDS.forEach((i) => {
  byId[i.id] = i;
});

// radial layout scale (1 = the authored compact layout)
const SPREAD = 1.0;
[...PROBES, ...ISLANDS].forEach((o) => {
  o.x = ROOT.x + (o.x - ROOT.x) * SPREAD;
  o.y = ROOT.y + (o.y - ROOT.y) * SPREAD;
});
const spread = (p) => ({ x: ROOT.x + (p.x - ROOT.x) * SPREAD, y: ROOT.y + (p.y - ROOT.y) * SPREAD });
const parentsOf = (isl) => isl.parents || [isl.parent];
const childrenOf = (id) => ISLANDS.filter((i) => parentsOf(i).includes(id));

const searchViz = (isl) => {
  const terms = (isl.terms || []).map((t) => `<span class="WSMONAD-lit-term">${t}</span>`).join('');
  const pg = '<span class="WSMONAD-pg"><i></i><i></i><i></i></span>';
  return (
    '<div class="WSMONAD-lit">' +
    `<div class="WSMONAD-lit-terms">${terms}</div>` +
    '<div class="WSMONAD-lit-mid">' +
    `<div class="WSMONAD-bk"><span class="WSMONAD-cover"></span><span class="WSMONAD-spine"></span>${pg}${pg}${pg}</div>` +
    `<div class="WSMONAD-lit-meta"><span class="WSMONAD-bk-chip"><span class="WSMONAD-bk-n">0</span> / ${isl.screen || 412} screened</span><span class="WSMONAD-bk-arrow">→ ${isl.refs ? isl.refs.length : 6} relevant</span></div>` +
    '</div>' +
    '</div>'
  );
};
const keptHtml = (isl) =>
  `<div class="WSMONAD-lit-keep"><div class="WSMONAD-keep-h">Keeping ${isl.refs.length} of 47 papers</div>` +
  isl.refs.map((r) => `<div class="WSMONAD-keep-row">${r}</div>`).join('') +
  '</div>';

const PLAN = [
  { key: 'p1', label: 'Gather the datasets' },
  { key: 'p2', label: 'Expression: DE, pathways, network' },
  { key: 'p3', label: 'Variants: annotate + model' },
  { key: 'p4', label: 'Validate in your cohort' },
  { key: 'p5', label: 'Corroborate in literature' },
  { key: 'p9', label: 'Integrate hub + variant' },
  { key: 'p6', label: 'Synthesize the mechanism' },
  { key: 'p8', label: 'Write report + figures' },
];

const el = (t, a) => {
  const e = document.createElementNS(NS, t);
  for (const k in a) e.setAttribute(k, a[k]);
  return e;
};
const gbOf = (sz) => {
  if (!sz) return 0;
  const n = parseFloat(sz);
  return /MB/.test(sz) ? n / 1024 : n;
};

// Wire the (already-rendered) flow scaffold inside `stage` and begin on scroll.
// Returns a cleanup that tears down timers, the rAF loop, listeners and spheres.
export default function runFlow(stage) {
  if (!stage) return () => {};
  const q = (sel) => stage.querySelector(sel);

  const inputLayer = q('#flow-input');
  const askText = q('#ask-text');
  const dropzone = q('#dropzone');
  const dzText = q('#dz-text');
  const runBtn = q('#run-btn');
  const cursor = q('#m-cursor');

  const cam = q('#fl-cam');
  // On phones the flow scene is shorter, so zoom in harder to keep the active
  // area legible.
  const isMobile =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(max-width: 720px)').matches;
  const ZOOM_MUL = isMobile ? 1.3 : 1;
  const MAX_ZOOM = isMobile ? 1.85 : 1.4;
  const linkLayer = q('#fl-links');
  const packetLayer = q('#fl-packets');
  const nodeLayer = q('#fl-nodes');
  const logEl = q('#fs-log');
  const treeEl = q('#fs-tree');
  const planList = q('#fp-list');
  const planCount = q('#fp-count');
  const steerInput = q('#fs-steer');
  const dmUsed = q('#dm-used');
  const dmFill = q('#dm-fill');
  // status + elapsed timer live in the MONAD center node (rebuilt each run),
  // so query them fresh rather than caching a stale element.
  const setStat = (t) => {
    const e = q('#root-stat');
    if (e) e.textContent = t;
  };

  // spheres mounted into static svgs (stopped on teardown); hub sphere is
  // rebuilt each run so it gets its own slot.
  const staticSphereStops = [];
  let hubStop = null;
  const mountStatic = (sel, opts) => {
    const svg = q(sel);
    if (!svg) return;
    svg.innerHTML = '';
    const stop = makePointCloud(svg, opts);
    if (stop) staticSphereStops.push(stop);
  };
  mountStatic('#fi-sigil', { N: 96, R: 84 });
  mountStatic('#chat-sigil', { N: 54, R: 84 });

  const nodes = {};
  const probeNodes = {};
  const timers = [];
  let rafId = 0;
  let runStart = 0;
  let lastSec = -1;
  const folders = {};
  let usedGB = 0;
  const at = (ms, fn) => timers.push(setTimeout(fn, ms));

  function cursorToEl(t, dur) {
    const tr = t.getBoundingClientRect();
    const lr = inputLayer.getBoundingClientRect();
    cursor.style.transition = dur
      ? `left ${dur}ms cubic-bezier(.4,0,.3,1), top ${dur}ms cubic-bezier(.4,0,.3,1), opacity .3s`
      : 'none';
    cursor.style.left = `${tr.left + tr.width / 2 - lr.left}px`;
    cursor.style.top = `${tr.top + tr.height / 2 - lr.top}px`;
  }
  function cursorCorner() {
    const lr = inputLayer.getBoundingClientRect();
    cursor.style.transition = 'none';
    cursor.style.left = `${lr.width * 0.86}px`;
    cursor.style.top = `${lr.height * 0.9}px`;
  }
  function typeQuestion() {
    let i = 0;
    askText.textContent = '';
    const iv = setInterval(() => {
      askText.textContent = QUESTION.slice(0, ++i);
      if (i >= QUESTION.length) clearInterval(iv);
    }, 42);
    timers.push({ _iv: iv });
  }

  function buildPlan() {
    planList.innerHTML = '';
    PLAN.forEach((p) => {
      const d = document.createElement('div');
      d.className = 'WSMONAD-fp-item';
      d.dataset.plan = p.key;
      d.innerHTML = `<span class="WSMONAD-fp-box"></span><span class="WSMONAD-fp-label">${p.label}</span>`;
      planList.appendChild(d);
    });
    planCount.textContent = `0 / ${PLAN.length}`;
    [...planList.children].forEach((c, i) => at(7200 + i * 150, () => c.classList.add('WSMONAD-in')));
  }
  function planActive(k) {
    const it = planList.querySelector(`[data-plan="${k}"]`);
    if (it) it.classList.add('WSMONAD-in', 'WSMONAD-active');
  }
  function cnt() {
    planCount.textContent = `${planList.querySelectorAll('.WSMONAD-fp-item.WSMONAD-done').length} / ${planList.querySelectorAll('.WSMONAD-fp-item').length}`;
  }
  function planDone(k) {
    const it = planList.querySelector(`[data-plan="${k}"]`);
    if (it && !it.classList.contains('WSMONAD-done')) {
      it.classList.remove('WSMONAD-active');
      it.classList.add('WSMONAD-done');
    }
    cnt();
  }
  function planAddSteered(k, label) {
    const d = document.createElement('div');
    d.className = 'WSMONAD-fp-item WSMONAD-steered WSMONAD-in WSMONAD-active';
    d.dataset.plan = k;
    d.innerHTML = `<span class="WSMONAD-fp-box"></span><span class="WSMONAD-fp-label">${label}</span>`;
    planList.appendChild(d);
    cnt();
  }

  function bumpStorage(sz) {
    usedGB += gbOf(sz);
    if (dmUsed) dmUsed.textContent = usedGB.toFixed(1);
    if (dmFill) dmFill.style.width = `${Math.min(100, (usedGB / STORAGE) * 100).toFixed(1)}%`;
  }
  function mkLine(name, ind, kind, size, check) {
    const d = document.createElement('div');
    d.className = `WSMONAD-ft-line WSMONAD-${kind}${check ? ' WSMONAD-new' : ''}`;
    let inner = '<span class="WSMONAD-ind">';
    for (let i = 0; i < ind; i += 1) inner += '<i></i>';
    inner += '</span>';
    inner += CHEV + (kind === 'dir' ? FOLDER_IC : FILE_IC) + `<span class="WSMONAD-nm">${name}</span>${size ? `<span class="WSMONAD-sz">${size}</span>` : ''}`;
    d.innerHTML = inner;
    requestAnimationFrame(() => d.classList.add('WSMONAD-in'));
    return d;
  }
  function ensureRoot() {
    if (folders.__root) return;
    folders.__root = treeEl.appendChild(mkLine('run-0492', 0, 'dir'));
  }
  function addFolder(name) {
    ensureRoot();
    if (folders[name]) return;
    folders[name] = { last: treeEl.appendChild(mkLine(name, 1, 'dir')) };
  }
  function addFile(folder, name, size, check) {
    addFolder(folder);
    const f = folders[folder];
    const line = mkLine(name, 2, 'file', size, check);
    treeEl.insertBefore(line, f.last.nextSibling);
    f.last = line;
    if (check) bumpStorage(size);
  }

  function buildRoot() {
    const fo = el('foreignObject', { x: ROOT.x - 160, y: ROOT.y - 130, width: 320, height: 260 });
    const wrap = document.createElement('div');
    wrap.className = 'WSMONAD-rt-wrap';
    const div = document.createElement('div');
    div.className = 'WSMONAD-fl-root WSMONAD-assemble';
    div.id = 'fl-root';
    div.innerHTML =
      '<div class="WSMONAD-rt-sphere"><svg id="hub-sigil" viewBox="-100 -100 200 200" xmlns="http://www.w3.org/2000/svg"></svg></div>' +
      '<div class="WSMONAD-rt-eye">MONAD</div><div class="WSMONAD-rt-goal">Mitochondrial decline · aged heart</div>' +
      '<div class="WSMONAD-rt-stat"><span class="WSMONAD-rt-status"><span class="WSMONAD-hp"></span><span id="root-stat">Planning</span></span><span class="WSMONAD-rt-timer" id="root-timer">0:00</span></div>';
    wrap.appendChild(div);
    fo.appendChild(wrap);
    nodeLayer.appendChild(fo);
    hubStop = makePointCloud(q('#hub-sigil'), { N: 84, R: 84, assemble: true, assembleMs: 2200 });
  }
  // status is driven by setStat (descriptive phase names); the older lowercase
  // rootStat() calls are now no-ops so the two don't fight over #root-stat.
  function rootStat() {}

  function pt(id) {
    if (id === 'root') return ROOT;
    return prById[id] || byId[id];
  }

  function buildProbe(p) {
    const A = { x: p.x, y: p.y };
    const B = { x: ROOT.x, y: ROOT.y };
    const ln = el('line', { class: 'WSMONAD-fl-link', x1: A.x, y1: A.y, x2: B.x, y2: B.y });
    linkLayer.appendChild(ln);
    const fo = el('foreignObject', { x: p.x - 102, y: p.y - 15, width: 204, height: 30 });
    const wrap = document.createElement('div');
    wrap.className = 'WSMONAD-agent-wrap';
    wrap.innerHTML = `<div class="WSMONAD-agent-node"><span class="WSMONAD-an-ic">${PROBE_IC}</span><span class="WSMONAD-an-label">${p.label}</span></div>`;
    fo.appendChild(wrap);
    nodeLayer.appendChild(fo);
    probeNodes[p.id] = { p, wrap, link: { ln, A, B } };
  }
  function buildIsland(isl) {
    const w = isl.w || CARD_W;
    const h = isl.h || CARD_H;
    const A = { x: isl.x, y: isl.y };
    // one input link per parent (combine nodes have several)
    const inLinks = parentsOf(isl).map((pid) => {
      const par = pt(pid);
      const B = { x: par.x, y: par.y };
      const ln = el('line', { class: 'WSMONAD-fl-link', x1: A.x, y1: A.y, x2: B.x, y2: B.y });
      linkLayer.appendChild(ln);
      return { ln, A, B, parentId: pid };
    });
    const fo = el('foreignObject', { x: isl.x - w / 2, y: isl.y - h / 2, width: w, height: h });
    const card = document.createElement('div');
    card.className = `WSMONAD-island${isl.steered ? ' WSMONAD-steered' : ''}`;
    card.dataset.type = isl.type;
    const sub = isl.src ? `<div class="WSMONAD-is-src">src: ${isl.src}</div>` : '';
    const vizInner = isl.type === 'SEARCH' ? searchViz(isl) : VIZ[isl.type] || '';
    card.innerHTML =
      `<div class="WSMONAD-is-top"><span class="WSMONAD-is-probe">${PROBE_IC}</span><span class="WSMONAD-is-ic">${ICON[isl.type] || ''}</span><span class="WSMONAD-is-type">${isl.type}</span><span class="WSMONAD-is-stat">queued</span></div>` +
      `<div class="WSMONAD-is-name">${isl.name}</div>${sub}<div class="WSMONAD-is-viz">${vizInner}</div><div class="WSMONAD-is-out">${isl.out || ''}</div>`;
    fo.appendChild(card);
    nodeLayer.appendChild(fo);
    nodes[isl.id] = {
      isl, card, inLinks, link: inLinks[0],
      bar: card.querySelector('.WSMONAD-viz-bar > span'), stat: card.querySelector('.WSMONAD-is-stat'),
      bkN: card.querySelector('.WSMONAD-bk-n'), packets: [], lastPacket: 0, state: 'hidden', dlStart: 0, bubble: null, bubbleFO: null,
    };
  }

  function showBubble(id) {
    const n = nodes[id];
    if (!n) return;
    const BW = 210;
    const BH = 150;
    const fo = el('foreignObject', { x: n.isl.x - BW / 2, y: n.isl.y - (n.isl.h || CARD_H) / 2 - 10 - BH, width: BW, height: BH, style: 'overflow:visible' });
    const anc = document.createElement('div');
    anc.className = 'WSMONAD-bubble-anchor';
    const b = document.createElement('div');
    b.className = 'WSMONAD-bubble WSMONAD-term';
    const io = n.isl.io
      ? `<div class="WSMONAD-bb-io"><span class="WSMONAD-io-in">in: <b>${n.isl.io[0]}</b></span><span class="WSMONAD-io-out">out: <b>${n.isl.io[1]}</b></span></div>`
      : '';
    b.innerHTML = `<div class="WSMONAD-bb-h"><span>${n.isl.name}</span><span class="WSMONAD-bb-pct">0%</span></div>${io}<div class="WSMONAD-bb-box"><div class="WSMONAD-bb-term"></div><div class="WSMONAD-bb-dl"><span></span></div></div>`;
    anc.appendChild(b);
    fo.appendChild(anc);
    nodeLayer.appendChild(fo);
    n.bubble = b;
    n.bubbleFO = fo;
    n.bbDl = b.querySelector('.WSMONAD-bb-dl > span');
    n.bbPct = b.querySelector('.WSMONAD-bb-pct');
    n.bbTerm = b.querySelector('.WSMONAD-bb-term');
    requestAnimationFrame(() => b.classList.add('WSMONAD-in'));
    if (n.isl.term) {
      n.isl.term.forEach((line, i) => {
        at(220 + i * 520, () => {
          if (!n.bbTerm) return;
          const d = document.createElement('div');
          d.className = `WSMONAD-ln${line[0] === 'out' ? ' WSMONAD-out' : ''}`;
          d.innerHTML = (line[0] && line[0] !== 'out' ? `<span class="WSMONAD-pr">${line[0]}</span>` : '') + line[1];
          n.bbTerm.appendChild(d);
        });
      });
    }
  }
  function bubbleDone(id) {
    const n = nodes[id];
    if (!n || !n.bubble) return;
    n.bubble.classList.add('WSMONAD-check');
    n.bubble.innerHTML = `<div class="WSMONAD-bb-h"><span>✓ ${n.isl.name}</span></div><div class="WSMONAD-bb-found">${n.isl.found || n.isl.out || ''}</div>`;
    at(1700, () => {
      if (n.bubble) n.bubble.classList.remove('WSMONAD-in');
      at(380, () => {
        if (n.bubbleFO && n.bubbleFO.parentNode) n.bubbleFO.remove();
        n.bubble = null;
        n.bbTerm = null;
      });
    });
  }

  let cnoteFO = null;
  function centerNote(text) {
    if (cnoteFO && cnoteFO.parentNode) cnoteFO.remove();
    const BW = 220;
    const BH = 34;
    const fo = el('foreignObject', { x: ROOT.x - BW / 2, y: ROOT.y - 78 - BH - 8, width: BW, height: BH, style: 'overflow:visible' });
    const c = document.createElement('div');
    c.className = 'WSMONAD-cnote';
    c.innerHTML = `<span class="WSMONAD-ck">✓</span><span>${text}</span>`;
    fo.appendChild(c);
    nodeLayer.appendChild(fo);
    cnoteFO = fo;
    requestAnimationFrame(() => c.classList.add('WSMONAD-in'));
    at(2000, () => {
      c.classList.remove('WSMONAD-in');
      at(380, () => {
        if (fo.parentNode) fo.remove();
        if (cnoteFO === fo) cnoteFO = null;
      });
    });
  }

  let camTarget = null;
  let camZoom = 1;
  const VIEW_CX = 548;
  const VIEW_CY = 320;
  function applyCam() {
    const pts = [ROOT];
    for (const id in probeNodes) if (probeNodes[id].wrap.classList.contains('WSMONAD-in')) pts.push(prById[id]);
    for (const id in nodes) if (nodes[id].state !== 'hidden') pts.push(byId[id]);
    let a = 1e9;
    let b = 1e9;
    let c = -1e9;
    let d = -1e9;
    pts.forEach((p) => {
      const w = (byId[p.id] && byId[p.id].w) || CARD_W;
      const h = (byId[p.id] && byId[p.id].h) || CARD_H;
      a = Math.min(a, p.x - w / 2);
      c = Math.max(c, p.x + w / 2);
      b = Math.min(b, p.y - h / 2);
      d = Math.max(d, p.y + h / 2);
    });
    const padX = 150;
    const padT = 200;
    const padB = 150;
    a -= padX;
    c += padX;
    b -= padT;
    d += padB;
    const bw = c - a;
    const bh = d - b;
    let s = Math.min((VB_W * 0.82) / bw, VB_H / bh);
    s = Math.max(0.24, Math.min(0.92, s));
    s = Math.max(0.16, Math.min(MAX_ZOOM, s * camZoom * ZOOM_MUL));
    let cx = (a + c) / 2;
    let cy = (b + d) / 2;
    if (camTarget) {
      cx += (camTarget.x - cx) * 0.22;
      cy += (camTarget.y - cy) * 0.22;
    }
    cam.setAttribute('transform', `translate(${(VIEW_CX - cx * s).toFixed(1)} ${(VIEW_CY - cy * s).toFixed(1)}) scale(${s.toFixed(3)})`);
  }
  function focusOn(node, zoom) {
    camTarget = node;
    camZoom = zoom || 1;
    applyCam();
  }

  function addLog(m) {
    const d = document.createElement('div');
    d.className = `WSMONAD-fs-msg ${m.cls ? 'WSMONAD-' + m.cls : ''}`;
    d.innerHTML = `<span class="WSMONAD-who">${m.who}</span><span class="WSMONAD-txt">${m.txt}</span>`;
    logEl.appendChild(d);
    while (logEl.children.length > 8) logEl.removeChild(logEl.firstChild);
    if (m.cls === 'user' && steerInput) typeSteer(m.txt.replace(/<[^>]+>/g, ''));
  }
  function typeSteer(t) {
    let i = 0;
    steerInput.value = '';
    const iv = setInterval(() => {
      steerInput.value = t.slice(0, ++i);
      if (i >= t.length) {
        clearInterval(iv);
        setTimeout(() => {
          steerInput.value = '';
        }, 1000);
      }
    }, 30);
    timers.push({ _iv: iv });
  }

  function spawnProbe(id) {
    const pn = probeNodes[id];
    if (!pn) return;
    pn.wrap.classList.add('WSMONAD-in');
    pn.link.ln.classList.add('WSMONAD-in', 'WSMONAD-live');
    addLog({ cls: 'plan', who: 'MONAD', txt: `deploys the ${pn.p.label.toLowerCase()}` });
    // no applyCam here — callers pan explicitly via focusOn, avoiding a double pan
  }
  // the plotting island renders each figure as its own panel, fanned around it
  function spawnPlots(n) {
    const cx = n.isl.x;
    const cy = n.isl.y;
    PLOTS.forEach((pl, i) => {
      at(360 + i * 470, () => {
        const W = 116;
        const H = 100;
        const px = cx + pl.dx;
        const py = cy + pl.dy;
        const ln = el('line', { class: 'WSMONAD-plot-link', x1: cx, y1: cy, x2: px, y2: py });
        linkLayer.appendChild(ln);
        const fo = el('foreignObject', { x: px - W / 2, y: py - H / 2, width: W, height: H, style: 'overflow:visible' });
        const card = document.createElement('div');
        card.className = 'WSMONAD-plot-card';
        card.innerHTML = `<div class="WSMONAD-plot-t">${pl.t}<span class="WSMONAD-plot-k">✓</span></div><div class="WSMONAD-plot-fig">${pl.svg}</div><div class="WSMONAD-plot-src">▤ ${pl.src}</div>`;
        fo.appendChild(card);
        nodeLayer.appendChild(fo);
        requestAnimationFrame(() => ln.classList.add('WSMONAD-in')); // card animates itself in on mount
      });
    });
  }
  // the report island visibly pulls in the literature + each figure as it composes
  function reportLinks(n) {
    const chart = byId.chart;
    const targets = [];
    if (byId.lit) targets.push({ x: byId.lit.x, y: byId.lit.y }); // literature scan → report
    if (chart) PLOTS.forEach((pl) => targets.push({ x: chart.x + pl.dx, y: chart.y + pl.dy }));
    targets.forEach((t, i) => {
      at(500 + i * 300, () => {
        const ln = el('line', { class: 'WSMONAD-report-link', x1: n.isl.x, y1: n.isl.y, x2: t.x, y2: t.y });
        linkLayer.appendChild(ln);
        requestAnimationFrame(() => ln.classList.add('WSMONAD-in'));
      });
    });
  }
  // citation pills appear to the RIGHT of the report island, one as each is cited
  function citePills(n) {
    const PW = 154;
    const PH = 22;
    const gap = 7;
    const x = n.isl.x + (n.isl.w || CARD_W) / 2 + 14;
    const y0 = n.isl.y - ((CITES.length - 1) * (PH + gap)) / 2 - PH / 2;
    CITES.forEach((c, i) => {
      at(900 + i * 440, () => {
        const py = y0 + i * (PH + gap);
        const ln = el('line', { class: 'WSMONAD-cite-link', x1: n.isl.x + (n.isl.w || CARD_W) / 2, y1: n.isl.y, x2: x, y2: py + PH / 2 });
        linkLayer.appendChild(ln);
        const fo = el('foreignObject', { x, y: py, width: PW, height: PH, style: 'overflow:visible' });
        const pill = document.createElement('div');
        pill.className = 'WSMONAD-cite-pill';
        pill.innerHTML = `<span class="WSMONAD-cite-dot"></span><span class="WSMONAD-cite-x">${c}</span>`;
        fo.appendChild(pill);
        nodeLayer.appendChild(fo);
        requestAnimationFrame(() => ln.classList.add('WSMONAD-in')); // pill animates itself in
      });
    });
  }
  function spawnIsland(id, opts) {
    const n = nodes[id];
    if (!n || n.state !== 'hidden') return;
    n.state = 'spawning'; // claim synchronously so a 2nd (multi-parent) trigger can't double-spawn
    n.card.classList.add('WSMONAD-in');
    // island card first, then draw its link(s) to the parent operative/output
    at(300, () => n.inLinks.forEach((l) => l.ln.classList.add('WSMONAD-in', 'WSMONAD-live')));
    if (n.isl.plan) planActive(n.isl.plan);
    const pnames = parentsOf(n.isl).map((p) => byId[p] && byId[p].name).filter(Boolean).join(' + ');
    if (pnames) addLog({ cls: 'act', who: 'Operative', txt: `reads ${pnames} → runs ${n.isl.name}` });
    else if (TERMINAL_TYPES.indexOf(n.isl.type) > -1) addLog({ cls: 'act', who: 'Operative', txt: `runs ${n.isl.name}` });
    if (n.isl.focus) focusOn({ x: n.isl.x, y: n.isl.y }, n.isl.focusZoom || 1);
    else if (!(opts && opts.noCam)) applyCam();
    at(360, () => {
      n.card.classList.add('WSMONAD-working');
      n.state = 'working';
      n.dlStart = performance.now();
      n.stat.textContent = n.isl.dl ? '0%' : 'working';
      if (TERMINAL_TYPES.indexOf(n.isl.type) > -1) showBubble(id);
      if (n.isl.id === 'chart') spawnPlots(n);
      if (n.isl.id === 'report') {
        reportLinks(n);
        citePills(n);
      }
    });
  }
  function completeIsland(n) {
    n.state = 'done';
    n.card.classList.remove('WSMONAD-working');
    n.card.classList.add('WSMONAD-done');
    n.inLinks.forEach((l) => {
      l.ln.classList.remove('WSMONAD-live');
      l.ln.classList.add('WSMONAD-done');
    });
    n.stat.textContent = 'done';
    if (n.bar) n.bar.style.width = '100%';
    if (n.bkN) n.bkN.textContent = n.isl.screen || 6;
    // literature island: reveal which references it is keeping
    if (n.isl.type === 'SEARCH' && n.isl.refs) {
      const v = n.card.querySelector('.WSMONAD-is-viz');
      if (v) v.innerHTML = keptHtml(n.isl);
    }
    bubbleDone(n.isl.id);
    if (n.isl.file) addFile(n.isl.file[0], n.isl.file[1], n.isl.file[2], true);
    if (n.isl.found) addLog({ cls: 'cite', who: `Found · ${n.isl.id}`, txt: n.isl.found });
    if (n.isl.note) centerNote(n.isl.note);
    if (n.isl.plan) {
      const sib = ISLANDS.filter((i) => i.plan === n.isl.plan && !i.late === !n.isl.late);
      if (sib.every((i) => nodes[i.id] && nodes[i.id].state === 'done')) planDone(n.isl.plan);
    }
    // visualization finished → only now summon the report operative + island (right)
    if (n.isl.id === 'chart') {
      at(700, () => {
        spawnProbe('op_report');
        focusOn(spread({ x: 660, y: 700 }), 1.05);
      });
      at(1300, () => spawnIsland('report'));
    }
    // report finished → resolve the whole run into MONAD
    if (n.isl.id === 'report') at(1600, finale);
    // spawn children once ALL of their parents have finished (combine nodes wait)
    childrenOf(n.isl.id).forEach((c, k) =>
      at(660 + k * 520, () => {
        if (c.late) return;
        if (parentsOf(c).every((pid) => nodes[pid] && nodes[pid].state === 'done')) spawnIsland(c.id);
      })
    );
  }

  function frame(now) {
    for (const id in nodes) {
      const n = nodes[id];
      if (n.state === 'working') {
        const dur = n.isl.dur || (n.isl.dl ? 2400 : 2600);
        const p = Math.min(1, (now - n.dlStart) / dur);
        if (n.bar) {
          n.bar.style.width = `${(p * 100).toFixed(0)}%`;
          n.stat.textContent = n.isl.dl ? `${Math.floor(p * 100)}%` : 'working';
        }
        if (n.bbDl) n.bbDl.style.width = `${(p * 100).toFixed(0)}%`;
        if (n.bbPct) n.bbPct.textContent = `${Math.floor(p * 100)}%`;
        if (n.bkN) n.bkN.textContent = Math.round(p * (n.isl.screen || 6));
        if (now - n.lastPacket > 430) {
          n.lastPacket = now;
          const out = n.isl.type === 'PULL';
          n.inLinks.forEach((l) => {
            const a = out ? l.A : l.B;
            const b = out ? l.B : l.A;
            const pk = el('circle', { class: `WSMONAD-fl-packet${out ? ' WSMONAD-out' : ''}`, r: 2.6, cx: a.x, cy: a.y });
            packetLayer.appendChild(pk);
            n.packets.push({ el: pk, a, b, start: now });
          });
        }
        if (p >= 1) completeIsland(n);
      }
      for (let i = n.packets.length - 1; i >= 0; i -= 1) {
        const pk = n.packets[i];
        const t = (now - pk.start) / 1000;
        if (t >= 1) {
          packetLayer.removeChild(pk.el);
          n.packets.splice(i, 1);
          continue;
        }
        pk.el.setAttribute('cx', (pk.a.x + (pk.b.x - pk.a.x) * t).toFixed(1));
        pk.el.setAttribute('cy', (pk.a.y + (pk.b.y - pk.a.y) * t).toFixed(1));
        pk.el.setAttribute('opacity', (1 - t * 0.5).toFixed(2));
      }
    }
    if (runStart) {
      const s = Math.floor((now - runStart) / 1000);
      if (s !== lastSec) {
        lastSec = s;
        const te = q('#root-timer');
        if (te) te.textContent = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
      }
    }
    rafId = requestAnimationFrame(frame);
  }

  function synthesize() {
    addLog({ cls: 'synth', who: 'MONAD', txt: 'Pulling every result back to the goal…' });
    planActive('p6');
    setStat('Synthesizing');
    focusOn(ROOT);
    rootStat('synthesizing');
    for (const id in nodes) if (nodes[id].state === 'done') nodes[id].inLinks.forEach((l) => l.ln.classList.add('WSMONAD-live'));
    at(950, () => {
      // only finished nodes — the report/viz islands spawn later and must not
      // have their connector lines shown before they (and their operatives) do
      for (const id in nodes) if (nodes[id].state === 'done') nodes[id].inLinks.forEach((l) => l.ln.classList.add('WSMONAD-done'));
      planDone('p6');
      addFile('workspace', 'hypothesis.md', '1 MB', true);
      const r = q('#fl-root');
      if (r) r.classList.add('WSMONAD-resolved');
      centerNote('Hypothesis ready');
      addLog({ cls: 'answer', who: 'Answer', txt: '<em>SIRT3</em> loss drives the decline — two destabilizing variants lower its activity, mitochondrial pathways collapse, and the drop reproduces in your cohort but not muscle.' });
      setStat('Writing report');
    });
    at(1600, () => {
      focusOn(spread({ x: 470, y: 770 }), 1.1); // pan down + slight zoom toward the working area
      spawnProbe('op_viz'); // visualization operative, left
    });
    at(2200, () => {
      spawnIsland('chart'); // plotting island fans its figures out below it
      rootStat('reporting');
    });
    // the report operative + island are summoned only once the visualization is done
    // (wired in completeIsland for the chart)
  }

  // the run resolves: everything is absorbed into MONAD, the panels collapse, a
  // checkmark blooms over the core, and the finished report slides in on the left
  function finale() {
    setStat('Complete');
    // 1. layerwise deconstruction: collect every floating node, then collapse
    //    the workspace into the core from the OUTSIDE IN, so it comes apart in
    //    concentric waves rather than every card shrinking at once.
    const items = [];
    nodeLayer.querySelectorAll('foreignObject').forEach((fo) => {
      if (fo.querySelector('#fl-root')) return;
      const cx = parseFloat(fo.getAttribute('x')) + parseFloat(fo.getAttribute('width')) / 2;
      const cy = parseFloat(fo.getAttribute('y')) + parseFloat(fo.getAttribute('height')) / 2;
      items.push({ fo, cx, cy, dist: Math.hypot(cx - ROOT.x, cy - ROOT.y) });
    });
    const maxD = items.reduce((m, it) => Math.max(m, it.dist), 1);
    items.forEach(({ fo, cx, cy, dist }) => {
      // outermost layer leaves first; each inner ring follows a wave behind
      const delay = Math.round((maxD - dist) * 0.95);
      // unwind toward the core: outer cards twist a touch more, left/right
      // counter-rotate so the structure visibly comes apart as it is drawn in
      const spin = (cx < ROOT.x ? -1 : 1) * (8 + (dist / maxD) * 9);
      // scale + rotate around the card's OWN centre (fill-box) so it converges
      // exactly ON the core, not to the left of it
      fo.style.transformBox = 'fill-box';
      fo.style.transformOrigin = 'center';
      fo.style.transition =
        `transform 0.8s ${delay}ms cubic-bezier(.5,0,.85,.3), opacity 0.46s ${delay + 300}ms ease-in`;
      fo.style.transform =
        `translate(${(ROOT.x - cx).toFixed(1)}px, ${(ROOT.y - cy).toFixed(1)}px) rotate(${spin.toFixed(1)}deg) scale(0.1)`;
      fo.style.opacity = '0';
    });
    // the connective web retracts a beat ahead of the nodes it joined
    linkLayer.querySelectorAll('line').forEach((ln) => {
      const ex = parseFloat(ln.getAttribute('x2'));
      const ey = parseFloat(ln.getAttribute('y2'));
      const dist = Math.hypot((ex || ROOT.x) - ROOT.x, (ey || ROOT.y) - ROOT.y);
      const delay = Math.max(0, Math.round((maxD - dist) * 0.95) - 90);
      ln.style.transition = `opacity 0.4s ${delay}ms ease`;
      ln.style.opacity = '0';
    });
    packetLayer.innerHTML = '';
    const s = 1.2;
    // 2. as the inner rings stream in, collapse the side panels and dive straight
    //    INTO the core (centred on ROOT)
    at(640, () => {
      ['plan', 'dir', 'side'].forEach((c) => {
        const p = stage.querySelector(`.WSMONAD-panel.WSMONAD-${c}`);
        if (p) p.classList.add('WSMONAD-collapsed');
      });
      cam.setAttribute('transform', `translate(${(VIEW_CX - ROOT.x * s).toFixed(1)} ${(VIEW_CY - ROOT.y * s).toFixed(1)}) scale(${s})`);
    });
    // 3. once everything has been taken in, the core resolves and a checkmark blooms
    at(1550, () => {
      const r = q('#fl-root');
      if (r) r.classList.add('WSMONAD-resolved');
      const g = el('g', { class: 'WSMONAD-fincheck' });
      g.appendChild(el('circle', { class: 'WSMONAD-fc-ring', cx: ROOT.x, cy: ROOT.y - 122, r: 15 }));
      g.appendChild(el('path', { class: 'WSMONAD-fc-tick', d: `M${ROOT.x - 6} ${ROOT.y - 122} l4.5 5 l8 -10.5` }));
      nodeLayer.appendChild(g);
    });
    // 4. THEN pan so MONAD shifts right, opening room for the report ISLAND on the
    //    left (the report is a real canvas node, so it pans + scales with the scene)
    at(2450, () => {
      cam.setAttribute('transform', `translate(${(720 - ROOT.x * s).toFixed(1)} ${(VIEW_CY - ROOT.y * s).toFixed(1)}) scale(${s})`);
    });
    at(2850, () => {
      const fo = el('foreignObject', { x: 42, y: 138, width: 216, height: 384, style: 'overflow:visible' });
      const rep = document.createElement('div');
      rep.className = 'WSMONAD-finale-report';
      rep.id = 'finale-report';
      rep.innerHTML =
        '<div class="WSMONAD-fr-doc">' +
          '<div class="WSMONAD-fr-h"><span class="WSMONAD-fr-dot"></span>report.md</div>' +
          '<div class="WSMONAD-fr-title">SIRT3 loss drives cardiac mitochondrial decline</div>' +
          '<div class="WSMONAD-fr-lines">' +
            '<i></i><i></i><i></i><i class="WSMONAD-fr-end"></i>' +
            '<i class="WSMONAD-fr-pp"></i><i></i><i></i><i class="WSMONAD-fr-end"></i>' +
            '<i class="WSMONAD-fr-pp"></i><i></i><i></i><i class="WSMONAD-fr-end"></i>' +
          '</div>' +
        '</div>' +
        '<div class="WSMONAD-fr-actions">' +
          '<button class="WSMONAD-fr-btn WSMONAD-fr-primary" type="button">View run <span class="WSMONAD-arr">↗</span></button>' +
          `<button class="WSMONAD-fr-btn" type="button">Download artifacts <span class="WSMONAD-fr-size" id="fr-size">${Math.max(1, usedGB).toFixed(1)} GB</span></button>` +
        '</div>';
      fo.appendChild(rep);
      nodeLayer.appendChild(fo);
      requestAnimationFrame(() => rep.classList.add('WSMONAD-in')); // island fades + settles in
    });
  }

  function clearTimers() {
    timers.forEach((t) => {
      if (t && t._iv) clearInterval(t._iv);
      else clearTimeout(t);
    });
    timers.length = 0;
  }
  function reset() {
    clearTimers();
    cancelAnimationFrame(rafId);
    if (hubStop) {
      hubStop();
      hubStop = null;
    }
    stage.dataset.stage = 'input';
    askText.textContent = '';
    if (steerInput) steerInput.value = '';
    dropzone.classList.remove('WSMONAD-hover');
    dzText.textContent = 'Drop your own data here';
    runBtn.classList.remove('WSMONAD-fire');
    inputLayer.querySelectorAll('.WSMONAD-dz-chip').forEach((c) => c.classList.remove('WSMONAD-in'));
    cursor.classList.remove('WSMONAD-in', 'WSMONAD-drag');
    cursor.style.opacity = '';
    cursorCorner();
    linkLayer.innerHTML = '';
    packetLayer.innerHTML = '';
    nodeLayer.innerHTML = '';
    logEl.innerHTML = '';
    treeEl.innerHTML = '';
    planList.innerHTML = '';
    for (const k in nodes) delete nodes[k];
    for (const k in probeNodes) delete probeNodes[k];
    for (const k in folders) delete folders[k];
    cnoteFO = null;
    camTarget = null;
    usedGB = 0;
    if (dmUsed) dmUsed.textContent = '0.0';
    if (dmFill) dmFill.style.width = '0%';
    setStat('Planning');
    runStart = 0;
    lastSec = -1;
    cam.setAttribute('transform', 'translate(0 0) scale(1)');
    // clear any finale state from a previous loop (the report island itself is
    // wiped with nodeLayer above; just restore the collapsed side panels)
    stage.querySelectorAll('.WSMONAD-panel.WSMONAD-collapsed').forEach((p) => p.classList.remove('WSMONAD-collapsed'));
    PROBES.forEach(buildProbe);
    ISLANDS.forEach(buildIsland);
    camTarget = ROOT;
    applyCam();
  }

  function play() {
    reset();
    at(600, typeQuestion);
    at(3200, () => {
      cursor.classList.add('WSMONAD-in', 'WSMONAD-drag');
      cursorCorner();
    });
    at(3350, () => cursorToEl(dropzone, 950));
    at(4350, () => {
      dropzone.classList.add('WSMONAD-hover');
      dzText.textContent = 'Release to attach';
    });
    at(5050, () => {
      dropzone.classList.remove('WSMONAD-hover');
      dzText.textContent = 'Attached · 2 files';
      cursor.classList.remove('WSMONAD-drag');
      inputLayer.querySelector('[data-file="1"]').classList.add('WSMONAD-in');
    });
    at(5300, () => inputLayer.querySelector('[data-file="2"]').classList.add('WSMONAD-in'));
    at(5700, () => cursorToEl(runBtn, 700));
    at(6450, () => runBtn.classList.add('WSMONAD-fire'));
    at(6700, () => {
      cursor.style.opacity = '0';
    });

    at(7000, () => {
      stage.dataset.stage = 'work';
      buildRoot(); // MONAD center assembles as the workspace reveals
      runStart = performance.now();
      rafId = requestAnimationFrame(frame);
      ensureRoot();
      addFolder('user_uploads');
      addFile('user_uploads', 'aged_cohort.fastq', '6.2 GB', true);
      addFile('user_uploads', 'variants.vcf', '0.4 GB', true);
      addFolder('database_downloads');
      addFolder('workspace');
      addLog({ cls: 'plan', who: 'MONAD', txt: 'Goal accepted. Drafting a plan.' });
      rootStat('planning');
    });
    buildPlan();

    const W = 7000;
    at(W + 800, () => {
      spawnProbe('op_data');
      focusOn(prById.op_data); // operative pops up → pan up, and that's it
    });
    at(W + 1500, () => spawnIsland('gtex', { noCam: true }));
    at(W + 1800, () => spawnIsland('clinvar', { noCam: true }));
    at(W + 2100, () => spawnIsland('intake', { noCam: true }));
    at(W + 3400, () => {
      setStat('Analyzing');
      spawnProbe('op_lit');
      focusOn(spread({ x: 900, y: 316 })); // operative appears → go right
    });
    at(W + 3900, () => {
      spawnIsland('lit', { noCam: true });
      focusOn(spread({ x: 1230, y: 256 })); // its island appears → a bit further right
    });
    at(W + 6400, () => focusOn(spread({ x: 260, y: -180 })));

    at(W + 8600, () => addLog({ cls: 'user', who: 'You', txt: 'also rule out skeletal-muscle — cardiac-specific?' }));
    at(W + 9300, () => {
      addLog({ cls: 'plan', who: 'MONAD', txt: 'Good call — gathering a muscle control.' });
      planAddSteered('p7', 'Rule out skeletal muscle');
      rootStat('steered');
      spawnIsland('muscle');
      focusOn(spread({ x: 140, y: 90 }));
    });

    at(W + 16000, synthesize);
    at(W + 34000, play);
  }

  let started = false;
  function maybeStart() {
    if (started) return;
    const r = stage.getBoundingClientRect();
    if (r.top < window.innerHeight * 0.85 && r.bottom > 0) {
      started = true;
      play();
    }
  }
  // The page may scroll inside a nested container (.WSMONAD-root), not the
  // window, so listen on the nearest scrollable ancestor as well as the window.
  function getScrollParent(el) {
    let p = el && el.parentElement;
    while (p) {
      const oy = getComputedStyle(p).overflowY;
      if (oy === 'auto' || oy === 'scroll') return p;
      p = p.parentElement;
    }
    return null;
  }
  const scrollParent = getScrollParent(stage);
  maybeStart();
  window.addEventListener('scroll', maybeStart, { passive: true });
  if (scrollParent) scrollParent.addEventListener('scroll', maybeStart, { passive: true });

  return () => {
    window.removeEventListener('scroll', maybeStart);
    if (scrollParent) scrollParent.removeEventListener('scroll', maybeStart);
    clearTimers();
    cancelAnimationFrame(rafId);
    if (hubStop) hubStop();
    staticSphereStops.forEach((s) => s && s());
  };
}
